import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useSkuMutationPrice from '@store/queries/sku/useSkuMutation.skuprice';
import useServiceSkuStore, { ServiceSkuState } from '@scenes/ServiceSkuPage/store/store.servicesku';
import { ZipValidationState } from '@components/Sku/ZipCodeEntry/ZipCodeEntry';
import segmentAnonymousId from '@utils/cookies/segmentAnonymousId';
import useIntervalConditionally from '@hooks/useIntervalConditionally';

/**
 * Fetches the split price of a SKU from the BE if we have:
 * 1. ZipCode
 * 2. ajs_anonymous_id
 *
 * @param {string} skuId
 * @param invalidateQueryKey - queryKey to invalidate the passed query key
 * @param mergeWithKey - queryKey to merge the query with
 */
type TConfig = {
  invalidateQueryKey?: any[];
  mergeWithKey?: any[];
};
const useBESplitMutationSkuPrice = (skuId: number, config: TConfig = {}) => {
  const { invalidateQueryKey, mergeWithKey } = config;
  const [ajsFound, setAjsFound] = useState(false);

  /* Hooks */
  const queryClient = useQueryClient();
  const zipValidationState = useServiceSkuStore((state: ServiceSkuState) => state.zipCodeGate.zipValidationState);
  const shouldRun = Boolean(skuId && zipValidationState === ZipValidationState.SUCCESS && !segmentAnonymousId() && !ajsFound);
  useIntervalConditionally(
    () => {
      const anonymousId = segmentAnonymousId();
      if (anonymousId) {
        setAjsFound(true);
        return true; // This will stop the interval once the anonymousId is found
      }
      return false; // This will keep the interval running
    },
    200,
    15,
    shouldRun,
  );

  const { mutate, data, isError, isPending, isSuccess } = useSkuMutationPrice({ skuId, config });

  useEffect(() => {
    if (isSuccess) {
      if (invalidateQueryKey) {
        queryClient.invalidateQueries({ queryKey: invalidateQueryKey });
      }

      if (mergeWithKey) {
        // 1. get existing query data
        const existingData = queryClient.getQueryData(mergeWithKey) || {};
        // 2. merge with existing query data
        const mergedData = { ...existingData, ...(data as Record<string, unknown>) };
        queryClient.setQueryData(mergeWithKey, mergedData);
      }
    }
  }, [isSuccess, data, invalidateQueryKey, mergeWithKey, queryClient]);

  /**
   * Lets see if the following is true:
   * 1. We have a skuId
   * 2. We have a zipCode
   * 3. We have ajs_anonymous_id
   *    Note: If we don't have ajs_anonymous_id, lets poll for it.
   */
  useEffect(() => {
    if (skuId && zipValidationState === ZipValidationState.SUCCESS) {
      /* Make sure ajs anonoymous cookie exists */
      if (segmentAnonymousId() || ajsFound) {
        mutate();
      }
    }
  }, [zipValidationState, ajsFound, skuId, invalidateQueryKey, mergeWithKey, queryClient, mutate]);

  return {
    data,
    isError,
    isPending,
  };
};

export default useBESplitMutationSkuPrice;
